import React, {useEffect, useState} from 'react'
import styled from 'styled-components';
import {useParams} from "react-router";
import Modal from "../../../../common/Modal";
import {Button, Form} from "react-bootstrap";
import RowSpacer from "../../../../common/styles/RowSpacer";
import {BooksitoutServer} from "../../../../config/BooksitoutServer";
import toast from "react-hot-toast";
import useBookDetailStore from "../useBookDetailStore";
import NumberInput from "../../../../common/form/NumberInput";
import DateForm from "../../../../common/form/DateForm";
import useReadingSessionStore from '../../reading/useReadingSessionStore';
import BottomCTA from "../../../../common/button/BottomCTA";
import booksitoutIcon from "../../../../config/BooksitoutIcon";

interface Props {
    isOpen: boolean
    close: () => void
}

const BookDetailReadingSessionAddModal: React.FC<Props> = ({isOpen, close}) => {
    const {bookId} = useParams()
    const {book, readingSessions, addReadingSession, updateBookStatus} = useBookDetailStore()
    const {playConfetti} = useReadingSessionStore()

    const [percent, setPercent] = useState<boolean>(false)
    const [startPercent, setStartPercent] = useState<string | null>(null)
    const [endPercent, setEndPercent] = useState<string | null>(null)

    useEffect(() => {
        setStartPage(book?.currentPage?.toString() ?? null)
        if (book?.currentPage != null && book?.endPage != null) {
            setStartPercent(String(Math.ceil((book.currentPage / book.endPage) * 100)))
        }
        if (readingSessions.length > 0) {
            const lastMethod = readingSessions[0]?.method
            if (lastMethod === 'PERCENTAGE') {
                setPercent(true)
            }
        }
    }, [book, readingSessions]);

    const [startPage, setStartPage] = useState<string | null>(null)
    const [endPage, setEndPage] = useState<string | null>(null)
    const [readTimeInMinutes, setReadTimeInMinutes] = useState<string>('')
    const [year, setYear] = useState<number>(new Date().getFullYear())
    const [month, setMonth] = useState<number>(new Date().getMonth() + 1)
    const [day, setDay] = useState<number>(new Date().getDate())

    const totalPage = Number(book?.endPage) || 0
    const startPercentToPage = totalPage ? Math.floor((Number(startPercent) / 100) * totalPage) : 0
    const endPercentToPage = totalPage ? Math.floor((Number(endPercent) / 100) * totalPage) : 0

    const addReadingSessionToServer = (isLast: boolean) => {
        const startPageComputed = percent
            ? (startPercent != null ? startPercentToPage : null)
            : startPage
        const endPageComputed = percent
            ? (endPercent != null ? endPercentToPage : null)
            : endPage

        const data = {
            startPage: startPageComputed,
            endPage: endPageComputed,
            readTimeInMinutes: readTimeInMinutes,
            method: percent ? 'PERCENTAGE' : 'PAGE',
            year: year,
            month: month,
            day: day,
            isLast: isLast,
        }

        BooksitoutServer
            .post(`/v1/book/${bookId}/reading-sessions`, data)
            .then((res) => {
                toast.success('독서활동을 추가했어요.')
                addReadingSession(res.data)
                if (isLast) {
                    playConfetti()
                    updateBookStatus('DONE')
                    toast.success('책을 다 읽었어요! 축하 드려요! 👏')
                }
                close()
            })
            .catch(() => toast.error('오류가 났어요. 잠시 후 다시 시도해 주세요.'))
    }

    const handleOnSubmit = (e: React.FormEvent<HTMLFormElement> | null) => {
        e?.preventDefault()

        if (book == null) {
            return
        }

        const hasEndInput = percent
            ? (endPercent != null && endPercent !== '')
            : (endPage != null && endPage !== '')

        if (!hasEndInput && readTimeInMinutes === '') {
            toast.error('독서시간이나 끝 페이지를 입력해 주세요.')
            return
        }

        const effectiveStartPage = percent ? startPercentToPage : Number(startPage)
        const effectiveEndPage = percent ? endPercentToPage : Number(endPage)

        if (hasEndInput && effectiveEndPage < effectiveStartPage) {
            toast.error('끝 페이지는 시작 페이지보다 커야 해요.')
            return
        }
        const isLast = book.endPage <= effectiveEndPage && window.confirm('책을 다 읽으신거 같아요. 완료 처리할까요?')

        addReadingSessionToServer(isLast)

        setReadTimeInMinutes('')
        setEndPage(null)
        setEndPercent(null)
    }

    // 독서시간 추측
    const [isReadTimeButtonDisabled, setIsReadTimeButtonDisabled] = useState<boolean>(true)
    useEffect(() => {
        if (startPage != null && endPage != null && Number(startPage) < Number(endPage) && readingSessions.length > 0 && (readTimeInMinutes == '' || readTimeInMinutes == '0')) {
            setIsReadTimeButtonDisabled(false)
        } else {
            setIsReadTimeButtonDisabled(true)
        }
    }, [startPage, endPage, readingSessions, readTimeInMinutes])
    const predictReadTime = () => {
        if (startPage == null || endPage == null || Number(startPage) >= Number(endPage) || readingSessions.length == 0) {
            toast.error(`정보가 충분하지 않아 독서시간을 추측할 수 없었어요. 독서활동을 더 기록하면 추측할 수 있어요.`)
            return
        }

        const totalPagesRead = readingSessions
            .filter((readingSession) => readingSession.startPage != null && readingSession.endPage != null)
            .reduce((acc, readingSession) => {
                return acc + (readingSession.endPage!! - readingSession.startPage!! + 1)
            }, 0)

        const totalTimeRead = readingSessions
            .filter((readingSession) => readingSession.startPage != null && readingSession.endPage != null)
            .reduce((acc, readingSession) => acc + readingSession.readTimeInMinutes, 0)

        const averageReadTimePerPage = totalTimeRead / totalPagesRead

        setReadTimeInMinutes(String(Math.floor((Number(endPage) - Number(startPage) + 1) * averageReadTimePerPage)))

        toast.success(`그 전 독서활동을 바탕으로 독서시간을 추측했어요.`)
    }

    // 페이지/퍼센트 추측
    const [isPredictButtonDisabled, setIsPredictButtonDisabled] = useState<boolean>(true)
    useEffect(() => {
        const hasReadTime = readTimeInMinutes != null && readTimeInMinutes != '0' && readTimeInMinutes != ''
        const hasNoEnd = percent
            ? (endPercent == null || endPercent == '')
            : (endPage == null || endPage == '')
        const hasStart = percent ? startPercent != null : startPage != null

        if (hasStart && hasReadTime && hasNoEnd && readingSessions.length > 0) {
            setIsPredictButtonDisabled(false)
        } else {
            setIsPredictButtonDisabled(true)
        }
    }, [startPage, endPage, startPercent, endPercent, readTimeInMinutes, percent])
    const predictEnd = () => {
        if (readTimeInMinutes == null || readTimeInMinutes == '0') {
            toast.error(`정보가 충분하지 않아 추측할 수 없었어요.`)
            return
        }

        const totalPagesRead = readingSessions
            .filter((readingSession) => readingSession.startPage != null && readingSession.endPage != null)
            .reduce((acc, readingSession) => {
                return acc + (readingSession.endPage!! - readingSession.startPage!! + 1)
            }, 0)

        const totalTimeRead = readingSessions
            .filter((readingSession) => readingSession.startPage != null && readingSession.endPage != null)
            .reduce((acc, readingSession) => acc + readingSession.readTimeInMinutes, 0)

        const averagePagePerMinute = totalPagesRead / totalTimeRead
        const predictedEndPage = Number(percent ? startPercentToPage : startPage) + Math.floor(Number(readTimeInMinutes) * averagePagePerMinute) - 1

        if (percent) {
            const predictedPercent = totalPage ? Math.min(100, Math.ceil((predictedEndPage / totalPage) * 100)) : 0
            setEndPercent(String(predictedPercent))
        } else {
            setEndPage(String(predictedEndPage))
        }

        toast.success(`그 전 독서활동을 바탕으로 추측했어요`)
    }

    return (
        <Modal
            isShowing={isOpen}
            onClose={close}
            titleText={'독서활동 추가하기'}
            size={'lg'}
            body={
                <Form onSubmit={handleOnSubmit}>
                    <Form.Group>
                        <Form.Label>독서시간 (분)</Form.Label>

                        <Row>
                            <Col10>
                                <NumberInput
                                    isFocusOnAppear
                                    placeholder={'독서시간 (분)'}
                                    min={1}
                                    max={1000}
                                    value={readTimeInMinutes}
                                    onChange={(e) => setReadTimeInMinutes(e.target.value)}
                                />
                            </Col10>

                            <Col2>
                                <Button
                                    style={{ backgroundColor: 'var(--secondary-btn-bg)', border: 'none', color: 'var(--text-color)', marginLeft: 'auto', borderRadius: '16px', fontWeight: '600' }}
                                    disabled={isReadTimeButtonDisabled}
                                    onClick={() => predictReadTime()}
                                >
                                    추측하기
                                </Button>
                            </Col2>
                        </Row>
                    </Form.Group>
                    <RowSpacer size={30}/>

                    <Form.Group>
                        <div className="d-flex justify-content-between align-items-center">
                            <Form.Label>{percent ? '시작 / 끝 퍼센트' : '시작 / 끝 페이지'}</Form.Label>
                            <button type="button" className="border-0 bg-transparent text-book" onClick={() => setPercent(!percent)}>
                                {percent ? (
                                    <booksitoutIcon.page style={{ fontSize: '26px' }} />
                                ) : (
                                    <booksitoutIcon.percent style={{ fontSize: '26px' }} />
                                )}
                            </button>
                        </div>

                        <Row>
                            {percent ? (
                                <>
                                    <Col5>
                                        <div className="d-flex" style={{ alignItems: 'center' }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <NumberInput
                                                    placeholder='시작 퍼센트'
                                                    value={startPercent}
                                                    min={0}
                                                    max={100}
                                                    onChange={(e) => setStartPercent(e.target.value)}
                                                />
                                            </div>
                                            <span className="text-book" style={{ marginLeft: '-65px', fontSize: '14px', whiteSpace: 'nowrap' }}>
                                                {Number.isFinite(startPercentToPage) ? startPercentToPage : 0}페이지
                                            </span>
                                        </div>
                                    </Col5>

                                    <Col5>
                                        <div className="d-flex" style={{ alignItems: 'center' }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <NumberInput
                                                    placeholder='끝 퍼센트'
                                                    min={startPercent ? Number(startPercent) : 0}
                                                    max={100}
                                                    value={endPercent}
                                                    onChange={(e) => setEndPercent(e.target.value)}
                                                    minErrorMessage={'끝 퍼센트는 시작 퍼센트보다 커야 해요.'}
                                                />
                                            </div>
                                            <span className="text-book" style={{ marginLeft: '-65px', fontSize: '14px', whiteSpace: 'nowrap' }}>
                                                {Number.isFinite(endPercentToPage) ? endPercentToPage : 0}페이지
                                            </span>
                                        </div>
                                    </Col5>
                                </>
                            ) : (
                                <>
                                    <Col5>
                                        <NumberInput
                                            placeholder='시작 페이지'
                                            value={startPage}
                                            min={1}
                                            max={(book?.endPage ?? 1) - 1}
                                            onChange={(e) => setStartPage(e.target.value)}
                                        />
                                    </Col5>

                                    <Col5>
                                        <NumberInput
                                            placeholder='끝 페이지'
                                            min={startPage ? Number(startPage) : 1}
                                            max={book?.endPage}
                                            value={endPage}
                                            onChange={(e) => setEndPage(e.target.value)}
                                            minErrorMessage={'끝 페이지는 시작 페이지보다 커야 해요.'}
                                        />
                                    </Col5>
                                </>
                            )}

                            <Col2>
                                <Button
                                    style={{ backgroundColor: 'var(--secondary-btn-bg)', border: 'none', color: 'var(--text-color)', marginLeft: 'auto', borderRadius: '16px', fontWeight: '600' }}
                                    disabled={isPredictButtonDisabled}
                                    onClick={() => predictEnd()}
                                >
                                    추측하기
                                </Button>
                            </Col2>
                        </Row>
                    </Form.Group>
                    <RowSpacer size={30}/>

                    <Form.Group>
                        <Form.Label>날짜</Form.Label>

                        <DateForm year={year} month={month} day={day} setYear={setYear} setMonth={setMonth}
                                  setDay={setDay}/>
                    </Form.Group>
                    <BottomCTA message={'추가하기'} onClick={() => handleOnSubmit(null)} disabled={
                        !(percent ? (endPercent != null && endPercent !== '') : (endPage != null && endPage !== '')) && readTimeInMinutes === ''
                    }/>
                    <button type={'submit'} className={'d-none'}/>
                </Form>
            }
        />
    )
}

const Row = styled.div.attrs({
    className: 'row'
})``

const Col2 = styled.div.attrs({
    className: 'col-12 col-md-2 mt-2 mt-md-0'
})`
    display: flex;
    justify-content: flex-end;

    @media (min-width: 768px) {
        & > button {
            width: 100%;
        }
    }
`

const Col5 = styled.div.attrs({
    className: 'col-12 col-md-5 mb-2 mb-md-0'
})``

const Col10 = styled.div.attrs({
    className: 'col-12 col-md-10 mb-2 mb-md-0'
})``


export default BookDetailReadingSessionAddModal
